import cx from 'classnames';
import {createContext, useContext, useMemo, useRef} from 'react';
import {createPortal} from 'react-dom';
import {CSSTransition} from 'react-transition-group';
import {Button} from '../atoms/Button';
import {Divider} from '../atoms/Divider';
import {Typography} from '../atoms/Typography';
import {ChevronIcon} from '../svg/ChevronIcon';
import {CloseLineIcon} from '../svg/CloseLineIcon';
import {formatHeaderTitle} from '../utils/utils';

export const BottomSheet = (props) => {
  const {title, onClose, 'data-qa-id': dataQaId = 'bottom-sheet'} = props;
  const fadeRef = useRef(null);
  const slideRef = useRef(null);

  const formattedTitle = useMemo(() => formatHeaderTitle(title), [title]);

  const params = {...props, title: formattedTitle, 'data-qa-id': dataQaId};

  return (
    <BottomSheetPortal {...params}>
      <Fade ref={fadeRef}>
        <div ref={fadeRef}>
          <Button
            variant="unstyled-button"
            className="bottom-sheet__overlay"
            onClick={onClose}
            aria-label="Close"
            data-qa-id={`${dataQaId}__overlay`}
          />
          <SlideUp ref={slideRef}>
            <Sheet ref={slideRef} {...params} />
          </SlideUp>
        </div>
      </Fade>
    </BottomSheetPortal>
  );
};

const BottomSheetPortalContext = createContext(null);

const BottomSheetPortal = (props) => {
  const {aboveOtherModals = true} = props;
  return createPortal(
    <BottomSheetPortalContext.Provider value={props}>
      <div
        className={cx('bottom-sheet__portal', {
          'bottom-sheet__portal__above-others': aboveOtherModals,
        })}
      >
        {props.children}
      </div>
    </BottomSheetPortalContext.Provider>,
    document.body
  );
};

const Fade = ({children, ref}) => {
  const {in: IN} = useContext(BottomSheetPortalContext);

  return (
    <CSSTransition in={IN} timeout={300} nodeRef={ref} classNames="fade" unmountOnExit>
      {children}
    </CSSTransition>
  );
};

const SlideUp = ({children, ref}) => {
  const {in: IN} = useContext(BottomSheetPortalContext);

  return (
    <CSSTransition in={IN} timeout={300} nodeRef={ref} classNames="slide-up" appear unmountOnExit>
      {children}
    </CSSTransition>
  );
};

const Sheet = ({
  content,
  title,
  onClose,
  className,
  'data-qa-id': dataQaId,
  tall = false,
  hideHeaderDivider = true,
  childProvidesSideMargin = false,
  showBackBtn = false,
  onBackBtnClick,
  showTitle = true,
  ref,
}) => (
  <div
    className={cx(
      'bottom-sheet',
      {tall, 'bottom-sheet--childProvidesSideMargin': childProvidesSideMargin},
      className
    )}
    ref={ref}
  >
    {title && (
      <div className="bottom-sheet__header">
        {showBackBtn && (
          <Button
            variant="unstyled-button"
            aria-label="Close"
            onClick={onBackBtnClick}
            data-qa-id={`${dataQaId}-heder-back-button`}
          >
            <ChevronIcon ratio={0.75} direction="left" />
          </Button>
        )}
        <Typography variant="header5" className="bottom-sheet__header-text">
          {title}
        </Typography>
        <Button
          variant="unstyled-button"
          aria-label="Close"
          onClick={onClose}
          data-qa-id={`${dataQaId}__header_close_button`}
        >
          <CloseLineIcon ratio={0.75} />
        </Button>
      </div>
    )}
    {!title && !showTitle && (
      <Button
        className="bottom-sheet__close-btn-absolute"
        variant="unstyled-button"
        aria-label="Close"
        onClick={onClose}
        data-qa-id={`${dataQaId}__header_close_button`}
      >
        <CloseLineIcon ratio={0.75} />
      </Button>
    )}
    {!hideHeaderDivider && <Divider />}
    {content ?? null}
  </div>
);
